import React, { useEffect, useMemo, useState } from "react"
import { Button, Input, Typography } from '@fullscreendigital/carrefour-dex-fe'
import { TitleHeader } from '../../../../Common/TitleHeader'
import {
  Container,
  Grid,
  Paper,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import { generatePath, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useMutation } from 'react-query'
import {
  makeCoreGetRequest,
  makeCorePutRequest,
  makeCorePostRequest
} from "../../../../../../../utils/api"
import { useNotification } from '../../../../../../../hooks'
import { formatErrorMessage } from '../../../../../../../utils/formatting'
import { URLS } from '../../../../constants'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/ro'
import { formatPayloadDate } from "../../../../../../../utils/helpers"
import { serializeUnorderedReceptionProduct, UnorderedProductForm, DSReceptionProductUnorderedReceivedDTO } from "./serializers"
import { normalizeProductCodeInput } from "../../../../helpers"

export type DSReceptionProductDTO = {
  id: number
  articleCode: number
  articleName: string
  eanCode: number
  scannedCode: string
  measureUnit: string
  receivedQuantity: string | number | null
  orderedQuantity: string | number | null
  difference: string | number | null
  expirationDate: string | [number, number, number] | null
  packagingUnit: string | number | null
  orderLineNumber: number | null
  unitsPerLogisticUnit: number | null
  pcbNumber: number | null
}

export type DSReceptionProductReceivedDTO = {
  receivedQuantity: number
  pcbNumber: number | null
  expirationDate: string | null
}

type ScanMode = 'KNOWN_PRODUCT' | 'UNORDERED_PRODUCT' | null

const toNumberOrNull = (v: string | number | null | undefined): number | null => {
  if (v === '' || v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const toDateInputValue = (
  d: string | [number, number, number] | null | undefined
): string => {
  if (!d) return ''

  if (Array.isArray(d)) {
    const [year, month, day] = d
    const mm = String(month).padStart(2, '0')
    const dd = String(day).padStart(2, '0')
    return `${year}-${mm}-${dd}`
  }

  if (typeof d === 'string') {
    const datePart = d.split('T')[0]

    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart
    }

    const parsed = new Date(d)
    if (!isNaN(parsed.getTime())) {
      const year = parsed.getFullYear()
      const month = String(parsed.getMonth() + 1).padStart(2, '0')
      const day = String(parsed.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    return ''
  }

  return ''
}

const isEmptyProductResponse = (data: any): boolean => {
  if (!data || typeof data !== 'object') return false

  return (
    data.orderLineNumber == null &&
    data.articleCode == null &&
    data.eanCode == null &&
    data.articleName == null
  )
}

const GoodsValidation: React.FC = () => {
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const [sp] = useSearchParams()
  const { id: orderId } = useParams()
  const supplierCode = sp.get('supplierCode')

  const [scannedCode, setScannedCode] = useState('')
  const [product, setProduct] = useState<DSReceptionProductDTO | null>(null)
  const [scanMode, setScanMode] = useState<ScanMode>(null)

  const [quantity, setQuantity] = useState<string>('')
  const [pcbNumber, setPcbNumber] = useState<string>('')
  const [unitsPerLogisticUnit, setUnitsPerLogisticUnit] = useState<string>('')
  const [dlc, setDlc] = useState<string>('')

  const [pendingUnexpectedScanCode, setPendingUnexpectedScanCode] = useState<string | null>(null)
  const [unexpectedWarningOpen, setUnexpectedWarningOpen] = useState(false)

  const [unorderedForm, setUnorderedForm] = useState<UnorderedProductForm>({
    scannedCode: '',
    measureUnit: '',
    receivedQuantity: '',
    unitsPerLogisticUnit: '',
    pcbNumber: '',
    articleName: '',
    expirationDate: ''
  })

  const [sessionSaved, setSessionSaved] = useState<Array<{
    articleCode: number | null
    articleName: string
    eanCode: number | string | null
    scannedCode: string
    qty: number
    dlc: string | null
    isAdditional: boolean
  }>>([])

  const canScan = useMemo(
    () => Boolean(orderId && supplierCode && scannedCode.trim().length > 0),
    [orderId, supplierCode, scannedCode]
  )

  const isKnownProduct = scanMode === 'KNOWN_PRODUCT' && !!product
  const isUnorderedProduct = scanMode === 'UNORDERED_PRODUCT'

  const resetKnownProductState = () => {
    setProduct(null)
    setQuantity('')
    setPcbNumber('')
    setUnitsPerLogisticUnit('')
    setDlc('')
  }

  const resetUnorderedForm = () => {
    setUnorderedForm({
      scannedCode: '',
      measureUnit: '',
      receivedQuantity: '',
      unitsPerLogisticUnit: '',
      pcbNumber: '',
      articleName: '',
      expirationDate: ''
    })
  }

  const resetAllFormState = () => {
    setProduct(null)
    setScanMode(null)
    setPendingUnexpectedScanCode(null)
    setQuantity('')
    setPcbNumber('')
    setUnitsPerLogisticUnit('')
    setDlc('')
    resetUnorderedForm()
  }

  const focusScanInput = () => {
    document.getElementById('scan-input')?.focus()
  }

  const applyKnownProduct = (data: DSReceptionProductDTO) => {
    setScanMode('KNOWN_PRODUCT')
    setProduct(data)
    setUnitsPerLogisticUnit(data.unitsPerLogisticUnit != null ? String(data.unitsPerLogisticUnit) : '')
    setPcbNumber('')
    setQuantity('')
    setDlc(toDateInputValue(data.expirationDate))
    resetUnorderedForm()
  }

  const applyUnorderedProductMode = (code: string) => {
    setScanMode('UNORDERED_PRODUCT')
    setProduct(null)
    setQuantity('')
    setPcbNumber('')
    setUnitsPerLogisticUnit('')
    setDlc('')
    setUnorderedForm({
      scannedCode: code,
      measureUnit: '',
      receivedQuantity: '',
      unitsPerLogisticUnit: '',
      pcbNumber: '',
      articleName: '',
      expirationDate: ''
    })
  }

  const scanMutation = useMutation(
    async () => {
      const trimmedCode = scannedCode.trim()
      const url = `/shop-reception-mobile/ds-reception-product/reception/${orderId}/${supplierCode}/${encodeURIComponent(trimmedCode)}`
      return await makeCoreGetRequest(url, null)
    },
    {
      onSuccess: (data: any) => {
        const trimmedCode = scannedCode.trim()

        if (isEmptyProductResponse(data)) {
          setPendingUnexpectedScanCode(trimmedCode)
          setUnexpectedWarningOpen(true)
          return
        }

        applyKnownProduct(data as DSReceptionProductDTO)
      },
      onError: () => {
        resetAllFormState()
        showNotification({ message: `Scan nereușit. Verifică dacă codul scanat este corect.`, type: 'error' })
      }
    }
  )

  useEffect(() => {
    if (!isKnownProduct) return
    if (pcbNumber.trim() === '') return

    const units = toNumberOrNull(unitsPerLogisticUnit)
    const pcb = toNumberOrNull(pcbNumber)

    if (units !== null && pcb !== null) {
      setQuantity(String(pcb * units))
    }
  }, [pcbNumber, unitsPerLogisticUnit, isKnownProduct])

  const pcbEditable = useMemo(
    () => isKnownProduct && toNumberOrNull(unitsPerLogisticUnit) !== null,
    [isKnownProduct, unitsPerLogisticUnit]
  )

  const saveKnownEnabled = useMemo(() => {
    if (!isKnownProduct) return false
    const q = toNumberOrNull(quantity)
    return !(q === null || q <= 0)
  }, [isKnownProduct, quantity])

  const saveUnorderedEnabled = useMemo(() => {
    if (!isUnorderedProduct) return false

    const receivedQuantity = toNumberOrNull(unorderedForm.receivedQuantity)
    const unitsPerLogisticUnit = toNumberOrNull(unorderedForm.unitsPerLogisticUnit)
    const pcbNumber = toNumberOrNull(unorderedForm.pcbNumber)

    return Boolean(
      unorderedForm.scannedCode.trim() &&
      unorderedForm.measureUnit.trim() &&
      unorderedForm.articleName.trim() &&
      receivedQuantity !== null && receivedQuantity > 0 &&
      unitsPerLogisticUnit !== null &&
      pcbNumber !== null
    )
  }, [isUnorderedProduct, unorderedForm])

  const saveKnownMutation = useMutation(
    async () => {
      const body: DSReceptionProductReceivedDTO = {
        receivedQuantity: toNumberOrNull(quantity)!,
        pcbNumber: toNumberOrNull(pcbNumber),
        expirationDate: dlc || null
      }

      const url = `/shop-reception-mobile/ds-reception-product/reception/${orderId}/${supplierCode}/${encodeURIComponent(scannedCode.trim())}?addQuantity=true`
      return await makeCorePutRequest(url, body)
    },
    {
      onSuccess: () => {
        const q = toNumberOrNull(quantity) ?? 0

        setSessionSaved(prev => [
          ...prev,
          {
            articleCode: product?.articleCode ?? null,
            articleName: product?.articleName ?? '—',
            eanCode: product?.eanCode ?? null,
            scannedCode: scannedCode.trim(),
            qty: q,
            dlc: dlc || null,
            isAdditional: false
          }
        ])

        resetAllFormState()
        setScannedCode('')
        focusScanInput()
        showNotification({ message: 'Produs salvat.', type: 'success' })
      },
      onError: (error: any) => {
        showNotification({ message: `Eroare la salvare. ${formatErrorMessage(error)}`, type: 'error' })
      }
    }
  )

  const saveUnorderedMutation = useMutation(
    async () => {
      const body: DSReceptionProductUnorderedReceivedDTO = serializeUnorderedReceptionProduct(unorderedForm)

      const url = `/shop-reception-mobile/ds-reception-product/reception/unordered/${orderId}/${supplierCode}`
      return await makeCorePostRequest(url, body)
    },
    {
      onSuccess: () => {
        const q = toNumberOrNull(unorderedForm.receivedQuantity) ?? 0

        setSessionSaved(prev => [
          ...prev,
          {
            articleCode: null,
            articleName: unorderedForm.articleName.trim(),
            eanCode: null,
            scannedCode: unorderedForm.scannedCode.trim(),
            qty: q,
            dlc: unorderedForm.expirationDate || null,
            isAdditional: true
          }
        ])

        resetAllFormState()
        setScannedCode('')
        focusScanInput()
        showNotification({ message: 'Produs necomandat salvat.', type: 'success' })
      },
      onError: (error: any) => {
        showNotification({ message: `Eroare la salvare produs necomandat. ${formatErrorMessage(error)}`, type: 'error' })
      }
    }
  )

  const handleScan = () => {
    if (!canScan) {
      showNotification({ message: 'Completează codul de scanare.', type: 'warning' })
      return
    }
    scanMutation.mutate()
  }

  const handleSaveKnown = () => {
    if (!saveKnownEnabled) return
    saveKnownMutation.mutate()
  }

  const handleSaveUnordered = () => {
    if (!saveUnorderedEnabled) return
    saveUnorderedMutation.mutate()
  }

  const handleConfirmUnexpected = () => {
    if (!pendingUnexpectedScanCode) return
    applyUnorderedProductMode(pendingUnexpectedScanCode)
    setPendingUnexpectedScanCode(null)
    setUnexpectedWarningOpen(false)
  }

  const handleCancelUnexpected = () => {
    setUnexpectedWarningOpen(false)
    setPendingUnexpectedScanCode(null)
    resetKnownProductState()
    setScanMode(null)
    setScannedCode('')
    focusScanInput()
  }

  const updateUnorderedField = (field: keyof UnorderedProductForm, value: string) => {
    setUnorderedForm(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const hasSessionItems = sessionSaved.length > 0

  if (!orderId) {
    return (
      <Container maxWidth="xs" style={{ marginTop: 20 }}>
        <Typography variant="medium">Lipseaște ID-ul comenzii. Te rog navighează din pasul anterior.</Typography>
      </Container>
    )
  }

  if (!supplierCode) {
    return (
      <Container maxWidth="xs" style={{ marginTop: 20 }}>
        <Typography variant="medium">Lipseaște supplierCode în URL. Te rog să transmiți supplierCode în query string când navighezi la acest pas.</Typography>
      </Container>
    )
  }

  return (
    <Container>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TitleHeader title="VALIDARE PRODUSE" />
        </Grid>

        <Grid item xs={12}>
          <Paper style={{ padding: 16 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Typography variant="medium" component="p" style={{ marginBottom: 8 }}>
                  Scanează produsul și confirmă detaliile.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Input
                  type="text"
                  variant="outlined"
                  label="Cod Produs"
                  value={scannedCode}
                  onChange={e => {
                    const value = normalizeProductCodeInput((e.target as HTMLInputElement).value)
                    setScannedCode(value)
                  }}
                  autoFocus
                  onKeyUp={event => {
                    if (event.which == 0) { handleScan() }
                  }}
                  id="scan-input"
                  width="100%"
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  label={scanMutation.isLoading ? 'Se scanează…' : 'Scanează'}
                  variant="contained"
                  onClick={handleScan}
                  disabled={!canScan || scanMutation.isLoading}
                  style={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {scanMutation.isLoading && (
          <Grid item xs={12}>
            <CircularProgress color="primary" style={{ margin: '16px auto', display: 'block' }} disableShrink />
          </Grid>
        )}

        {isKnownProduct && product && (
          <>
            <Grid item xs={12}>
              <Paper style={{ padding: 16 }}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="medium" component="p">
                      <strong>Articol:</strong> {product.articleCode} &nbsp;|&nbsp;
                      <strong>EAN:</strong> {product.eanCode}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="medium" component="p">
                      <strong>UM:</strong> {product.measureUnit || '—'} &nbsp;|&nbsp;
                      <strong>Linie comandă:</strong> {product.orderLineNumber ?? '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="medium" component="p">
                      <strong>Nume:</strong> {product.articleName ?? '—'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Input
                type="number"
                label="Numar PCB receptionate"
                variant="standard"
                width="100%"
                value={pcbNumber}
                onChange={e => setPcbNumber((e.target as HTMLInputElement).value)}
                disabled={!pcbEditable}
              />
            </Grid>

            <Grid item xs={12}>
              <Input
                type="number"
                label="Numar produse in PCB"
                variant="standard"
                width="100%"
                value={unitsPerLogisticUnit}
                onChange={() => {}}
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <Input
                type="number"
                label="Cantitate recepționată"
                variant="standard"
                width="100%"
                value={quantity}
                onChange={e => setQuantity((e.target as HTMLInputElement).value)}
              />
            </Grid>

            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ro">
                <DatePicker
                  label="DLC"
                  format="DD/MM/YYYY"
                  value={dlc ? dayjs(dlc, 'YYYY-MM-DD') : null}
                  onChange={(val: Dayjs | null) => setDlc(val ? val.format('YYYY-MM-DD') : '')}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <Button
                type="button"
                variant="contained"
                label={saveKnownMutation.isLoading ? 'Se salvează…' : 'Confirmă produsul'}
                onClick={handleSaveKnown}
                disabled={!saveKnownEnabled || saveKnownMutation.isLoading}
                style={{ width: '100%' }}
              />
            </Grid>
          </>
        )}

        {isUnorderedProduct && (
          <>
            <Grid item xs={12}>
              <Paper style={{ padding: 16, background: '#fff3cd' }}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="medium" component="p" style={{ color: '#b26a00', fontWeight: 700 }}>
                      Produs suplimentar / necomandat
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="light" component="p">
                      Completează datele necesare pentru înregistrarea produsului necomandat.
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Input
                type="text"
                label="Cod scanat"
                variant="standard"
                width="100%"
                value={unorderedForm.scannedCode}
                onChange={e => updateUnorderedField('scannedCode', (e.target as HTMLInputElement).value)}
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <Input
                type="text"
                label="Denumire produs"
                variant="standard"
                width="100%"
                value={unorderedForm.articleName}
                onChange={e => updateUnorderedField('articleName', (e.target as HTMLInputElement).value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Input
                type="select"
                label="UM"
                variant="standard"
                width="100%"
                value={unorderedForm.measureUnit}
                values={[
                  { label: 'Buc', value: 'A' },
                  { label: 'Kg', value: 'KG' }
                ]}
                onChange={e => updateUnorderedField('measureUnit', (e.target as HTMLInputElement).value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Input
                type="number"
                label="Cantitate recepționată"
                variant="standard"
                width="100%"
                value={unorderedForm.receivedQuantity}
                onChange={e => updateUnorderedField('receivedQuantity', (e.target as HTMLInputElement).value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Input
                type="number"
                label="Produse per PCB"
                variant="standard"
                width="100%"
                value={unorderedForm.unitsPerLogisticUnit}
                onChange={e => updateUnorderedField('unitsPerLogisticUnit', (e.target as HTMLInputElement).value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Input
                type="number"
                label="Număr PCB"
                variant="standard"
                width="100%"
                value={unorderedForm.pcbNumber}
                onChange={e => updateUnorderedField('pcbNumber', (e.target as HTMLInputElement).value)}
              />
            </Grid>

            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ro">
                <DatePicker
                  label="DLC"
                  format="DD/MM/YYYY"
                  value={unorderedForm.expirationDate ? dayjs(unorderedForm.expirationDate, 'YYYY-MM-DD') : null}
                  onChange={(val: Dayjs | null) => updateUnorderedField('expirationDate', formatPayloadDate(val))}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <Button
                type="button"
                variant="contained"
                label={saveUnorderedMutation.isLoading ? 'Se salvează…' : 'Confirmă produsul'}
                onClick={handleSaveUnordered}
                disabled={!saveUnorderedEnabled || saveUnorderedMutation.isLoading}
                style={{ width: '100%' }}
              />
            </Grid>
          </>
        )}

        {hasSessionItems && (
          <Grid item xs={12}>
            <Paper style={{ padding: 8 }}>
              <Typography variant="medium" component="p" style={{ margin: '8px 0 12px' }}>
                Produse salvate în această sesiune
              </Typography>

              <TableContainer style={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Articol</TableCell>
                      <TableCell>EAN</TableCell>
                      <TableCell align="right">Cant.</TableCell>
                      <TableCell>DLC</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessionSaved.map((it, idx) => (
                      <TableRow
                        key={`${it.scannedCode}-${idx}`}
                        style={it.isAdditional ? { backgroundColor: '#fff3cd' } : undefined}
                      >
                        <TableCell>
                          {it.isAdditional && (
                            <div style={{ fontWeight: 700, color: '#b26a00', marginBottom: 4 }}>
                              Produs suplimentar
                            </div>
                          )}
                          {it.articleName}
                        </TableCell>
                        <TableCell>{it.eanCode ?? it.scannedCode}</TableCell>
                        <TableCell align="right">{it.qty}</TableCell>
                        <TableCell>{it.dlc ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          <Button
            type="button"
            variant="outlined"
            label="Am terminat"
            style={{ width: '100%' }}
            onClick={() => {
              navigate(generatePath(URLS.AUTH_SUPPLIERS_DELIVERY_VALIDATION, { id: orderId }) + `?supplierCode=${supplierCode}`)
            }}
          />
        </Grid>
      </Grid>

      <Dialog
        open={unexpectedWarningOpen}
        onClose={handleCancelUnexpected}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Atenție</DialogTitle>
        <DialogContent>
          <Typography variant="medium" component="p">
            Produsul scanat nu se regăsește în comanda inițială. Doriți să continuați recepția?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            label="Renunță"
            onClick={handleCancelUnexpected}
          />
          <Button
            variant="contained"
            label="Continuă"
            onClick={handleConfirmUnexpected}
          />
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default GoodsValidation
